<template>
  <div class="date-picker">
    <VueDatePicker
      v-model="model"
      :locale="plLocaleWithCapitalizedMonths"
      format="dd.MM.yyyy"
      :auto-apply="true"
      :clearable="false"
      :close-on-auto-apply="true"
      :max-date="maxDate"
      :min-date="minDate"
      :config="{ monthChangeOnScroll: false }"
      :hide-navigation="['time', 'hours', 'minutes', 'seconds']"
      :day-names="dayNames"
      :text-input="false"
      :disabled-dates="computedDisabledDates"
      :disabled="disabled"
      :ui="uiConfig"
      :hide-offset-dates="true"
      :six-weeks="true"
      inline
    />
  </div>
</template>

<script lang="ts" setup>
  import { VueDatePicker } from '@vuepic/vue-datepicker'
  import type { Month } from 'date-fns'
  import { differenceInCalendarDays, isDate, parse } from 'date-fns'
  import { pl } from 'date-fns/locale'
  import { computed } from 'vue'

  import '@vuepic/vue-datepicker/dist/main.css'

  import { F, G } from '@mobily/ts-belt'
  import { match } from 'ts-pattern'

  const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

  const plLocaleWithCapitalizedMonths = {
    ...pl,
    localize: {
      ...pl.localize,
      month: (value: Month, options?: Parameters<typeof pl.localize.month>[1]) => {
        const result = pl.localize.month(value, options)
        return capitalizeFirstLetter(result)
      },
    },
  }

  type DatePickerProps = {
    modelValue: string | Date | undefined
    getDateStatus?: (date: Date) => 'blocked' | 'diet' | null
    disabledDates?: (date: Date) => boolean
    maxDate?: Date
    minDate?: Date
    disabled?: boolean
    selectionSpanInDays?: number
  }

  const props = defineProps<DatePickerProps>()
  const emit = defineEmits<(e: 'update:modelValue', value: Date | undefined) => void>()

  const dayNames = ['P', 'W', 'Ś', 'C', 'P', 'S', 'N']

  const computedDisabledDates = computed(() => {
    if (props.disabledDates) {
      return props.disabledDates
    }
    if (props.getDateStatus) {
      return (date: Date) => {
        const status = props.getDateStatus?.(date)
        return status === 'blocked' || status === 'diet'
      }
    }
    return undefined
  })

  type SelectedRange = 'start' | 'middle' | 'end' | 'single' | null

  const getSelectedRange = (
    date: Date,
    selectedDate: Date | undefined,
    selectionSpanInDays?: number,
  ): SelectedRange => {
    if (!selectedDate || !selectionSpanInDays || selectionSpanInDays < 2) {
      return null
    }

    const startDate = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
    )
    const cellDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const dayOffset = differenceInCalendarDays(cellDate, startDate)

    if (dayOffset < 0 || dayOffset >= selectionSpanInDays) {
      return null
    }

    const dayOfWeek = cellDate.getDay()
    const isStartOfWeek = dayOfWeek === 1
    const isEndOfWeek = dayOfWeek === 0
    const hasPreviousDayInRow = dayOffset > 0 && !isStartOfWeek
    const hasNextDayInRow = dayOffset < selectionSpanInDays - 1 && !isEndOfWeek

    if (!hasPreviousDayInRow && !hasNextDayInRow) {
      return 'single'
    }

    if (!hasPreviousDayInRow) {
      return 'start'
    }

    if (!hasNextDayInRow) {
      return 'end'
    }

    return 'middle'
  }

  const uiConfig = computed(() => {
    if (!props.getDateStatus && !props.selectionSpanInDays) return undefined

    return {
      dayClass: (date: Date) => {
        const classes: string[] = []

        if (props.getDateStatus) {
          classes.push(
            match(props.getDateStatus(date))
              .with('blocked', () => 'dp__cell--blocked')
              .with('diet', () => 'dp__cell--diet')
              .otherwise(() => 'dp__cell--available'),
          )
        }

        const selectedRange = getSelectedRange(date, model.value, props.selectionSpanInDays)

        if (selectedRange) {
          classes.push(`dp__cell--range-${selectedRange}`)
        }

        return classes.join(' ')
      },
    }
  })

  const model = computed({
    get() {
      return match(props.modelValue)
        .when(G.isString, value => {
          if (value.includes('-')) {
            return parse(value, 'dd-MM-yyyy', new Date())
          }
          if (value.includes('.')) {
            return parse(value, 'dd.MM.yyyy', new Date())
          }
          return undefined
        })
        .when(isDate, F.identity)
        .otherwise(() => undefined)
    },
    set(value: Date | undefined) {
      emit('update:modelValue', value)
    },
  })
</script>

<style scoped>
  .date-picker {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
  }
</style>

<style>
  .date-picker .dp--theme-light,
  .date-picker--inline .dp--theme-light {
    --dp-font-family: "Inter", sans-serif;
    --dp-cell-border-radius: 24px;
    --dp-primary-color: var(--color-main-100);
    --dp-range-color: var(--color-green-900);
    --dp-range-text-color: #fff;
    --dp-calendar-gap: 8px;
    --dp-border-color: var(--color-tertiary-text);
    --dp-border-color-hover: var(--color-main-100);
    --dp-border-color-focus: var(--color-main-100);
  }

  .date-picker .dp--calendar-row,
  .date-picker--inline .dp--calendar-row {
    gap: var(--dp-calendar-gap);
  }

  .date-picker .dp--calendar-header,
  .date-picker--inline .dp--calendar-header {
    gap: 8px;
  }

  .dp__cell--blocked {
    color: var(--color-text-text-disabled, #9ca3af) !important;
    text-decoration: line-through !important;
    cursor: not-allowed !important;
    background-color: var(--color-background-bg-primary, #fff) !important;
  }

  .dp__cell--blocked:hover {
    background-color: transparent !important;
  }

  .dp__cell--diet {
    color: #5d4037 !important;
    cursor: not-allowed !important;
    background-color: #ffecb3 !important;
  }

  .dp__cell--available {
    color: #2e7d32 !important;
    background-color: #e8f5e9 !important;
  }

  .date-picker .dp--active,
  .date-picker--inline .dp--active {
    color: var(--dp-primary-text-color, #fff) !important;
    background-color: var(--dp-primary-color, var(--color-main-100)) !important;
  }

  .date-picker .dp--cell-inner.dp__cell--range-start,
  .date-picker .dp--cell-inner.dp__cell--range-middle,
  .date-picker .dp--cell-inner.dp__cell--range-end,
  .date-picker .dp--cell-inner.dp__cell--range-single,
  .date-picker--inline .dp--cell-inner.dp__cell--range-start,
  .date-picker--inline .dp--cell-inner.dp__cell--range-middle,
  .date-picker--inline .dp--cell-inner.dp__cell--range-end,
  .date-picker--inline .dp--cell-inner.dp__cell--range-single {
    position: relative;
    color: var(--dp-range-text-color, #fff) !important;
    background-color: var(--dp-range-color, var(--color-green-900)) !important;
    border: 0 !important;
  }

  .date-picker .dp--cell-inner.dp__cell--range-start,
  .date-picker--inline .dp--cell-inner.dp__cell--range-start {
    border-radius: 999px 0 0 999px !important;
  }

  .date-picker .dp--cell-inner.dp__cell--range-end,
  .date-picker--inline .dp--cell-inner.dp__cell--range-end {
    border-radius: 0 999px 999px 0 !important;
  }

  .date-picker .dp--cell-inner.dp__cell--range-middle,
  .date-picker--inline .dp--cell-inner.dp__cell--range-middle {
    border-radius: 0 !important;
  }

  .date-picker .dp--cell-inner.dp__cell--range-start::after,
  .date-picker .dp--cell-inner.dp__cell--range-middle::before,
  .date-picker .dp--cell-inner.dp__cell--range-middle::after,
  .date-picker .dp--cell-inner.dp__cell--range-end::before,
  .date-picker--inline .dp--cell-inner.dp__cell--range-start::after,
  .date-picker--inline .dp--cell-inner.dp__cell--range-middle::before,
  .date-picker--inline .dp--cell-inner.dp__cell--range-middle::after,
  .date-picker--inline .dp--cell-inner.dp__cell--range-end::before {
    position: absolute;
    top: 0;
    bottom: 0;
    pointer-events: none;
    content: "";
    background-color: var(--dp-range-color, var(--color-green-900));
  }

  .date-picker .dp--cell-inner.dp__cell--range-start::after,
  .date-picker .dp--cell-inner.dp__cell--range-middle::after,
  .date-picker--inline .dp--cell-inner.dp__cell--range-start::after,
  .date-picker--inline .dp--cell-inner.dp__cell--range-middle::after {
    right: calc(var(--dp-calendar-gap) * -1);
    width: calc(var(--dp-calendar-gap) + 2px);
  }

  .date-picker .dp--cell-inner.dp__cell--range-middle::before,
  .date-picker .dp--cell-inner.dp__cell--range-end::before,
  .date-picker--inline .dp--cell-inner.dp__cell--range-middle::before,
  .date-picker--inline .dp--cell-inner.dp__cell--range-end::before {
    left: calc(var(--dp-calendar-gap) * -1);
    width: calc(var(--dp-calendar-gap) + 2px);
  }
</style>
